import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, merge, Observable } from 'rxjs';
import { normalizeBoardFilterData } from '../../board-filters.utils';
import {
  BoardField,
  BoardFilterCondition,
  BoardFilterFieldType,
  BoardFilterMode,
  BoardFilterSet,
  BoardFilterSetData,
  BoardFilterViewSettings,
  BoardManager,
  SaveBoardFilterPayload,
} from '../../board-v3.types';
import { BoardService } from '../../../../services/board.service';
import { BoardContactService } from '../../../../services/board-contact.service';
import { BoardCompanyService } from '../../../../services/board-company.service';
import { NotificationService } from '../../../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { SelectType } from '../../../select/select.interface';
import {
  DraftCondition,
  FilterEntityMode,
  FilterFieldOption,
  FilterOperator,
  FILTER_BOOLEAN_OPTIONS,
  FILTER_MODE_OPTIONS,
  FILTER_OPERATORS,
  filterFieldKey,
  getSystemFilterFields,
  operatorsForField,
  toFilterFieldType,
} from './filters.config';
import { deserializeFilterCondition, serializeFilterCondition, serializeFilterDraft } from './filters.utils';

interface FilterApi {
  getFilters(): Observable<BoardFilterSet[]>;
  createFilter(payload: SaveBoardFilterPayload & { type: string }): Observable<BoardFilterSet>;
  updateFilter(id: string, payload: SaveBoardFilterPayload & { type: string }): Observable<BoardFilterSet>;
  deleteFilter(id: string): Observable<void>;
}

export interface BoardV3FiltersResult {
  filterId: string | null;
  data: BoardFilterSetData;
  name: string;
}

@Component({
  selector: 'app-board-v3-filters',
  templateUrl: './filters.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3FiltersComponent implements OnInit, OnChanges {
  readonly SelectType = SelectType;
  readonly booleanOptions = FILTER_BOOLEAN_OPTIONS;
  @Input() funnelId: string | null = null;
  @Input() entityMode: FilterEntityMode = 'card';
  @Input() funnelType: string = '';
  @Input() fields: BoardField[] = [];
  @Input() managers: BoardManager[] = [];
  @Input() activeData: BoardFilterSetData = normalizeBoardFilterData();
  @Input() activeFilterId: string | null = null;

  @Output() apply = new EventEmitter<BoardV3FiltersResult>();
  @Output() closeChange = new EventEmitter<void>();

  private readonly boardService = inject(BoardService);
  private readonly contactService = inject(BoardContactService);
  private readonly companyService = inject(BoardCompanyService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  loading = true;
  initialized = false;
  allFields: FilterFieldOption[] = [];
  fieldSelectOptions: { label: string; value: string }[] = [];
  managerOptions: { id: string; name: string; avatarUrl: string }[] = [];
  private fieldsByKey = new Map<string, FilterFieldOption>();
  private valueOptionsByKey = new Map<string, { label: string; value: string }[]>();
  saving = false;
  deleting = false;
  filterSets: BoardFilterSet[] = [];
  selectedFilterId: string | null = null;
  draftName = '';
  draftShared = false;
  private savedDraftSnapshot = '';
  mode: BoardFilterMode = 'AND';
  view: BoardFilterViewSettings = normalizeBoardFilterData().view;
  conditions: DraftCondition[] = [];

  readonly modeOptions = FILTER_MODE_OPTIONS;

  readonly baseOperators = FILTER_OPERATORS;

  ngOnInit(): void {
    this.rebuildFieldOptions();
    merge(this.translateService.onLangChange, this.translateService.onTranslationChange)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.rebuildFieldOptions());
    this.resetDraftFromData(this.activeData);
    this.loadFilterSets();
    if (this.entityMode === 'company' && !this.managers.length) {
      this.boardService.getManagers({ take: 1000, page: 1 })
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: managers => {
            this.managers = managers;
            this.updateManagerOptions();
          },
          error: () => this.notificationService.showError('', String(this.translateService.instant('board_v3_filter.load_failed'))),
        });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fields']) this.rebuildFieldOptions();
    if (changes['managers']) {
      this.updateManagerOptions();
    }
    if (changes['activeData'] && !changes['activeData'].firstChange) {
      this.resetDraftFromData(this.activeData);
    }

    if (changes['funnelId'] && !changes['funnelId'].firstChange) {
      this.selectedFilterId = null;
      this.loadFilterSets();
    }
  }

  private updateManagerOptions(): void {
    this.managerOptions = this.managers.map(manager => ({
      id: manager.id,
      avatarUrl: manager.avatar_url,
      name: [manager.first_name, manager.last_name].filter(Boolean).join(' ') || manager.email || manager.phone || manager.id,
    }));
  }

  private rebuildFieldOptions(): void {
    const known = new Set<string>();
    const result: FilterFieldOption[] = [];
    const addField = (field: BoardField, filterType?: BoardFilterFieldType): void => {
      const key = this.fieldKey(field);
      if (!key || known.has(key)) return;
      known.add(key);
      result.push({ ...field, filter_type: filterType || toFilterFieldType(field) });
    };

    getSystemFilterFields(this.entityMode).forEach((field) => addField(field));
    this.fields.forEach((field) => addField(field));
    this.allFields = result;
    this.fieldsByKey = new Map(result.map(field => [this.fieldKey(field), field]));
    this.fieldSelectOptions = result.map(field => ({ label: this.fieldLabel(field), value: this.fieldKey(field) }));
    this.valueOptionsByKey = new Map(result.map(field => [
      this.fieldKey(field), this.fieldOptions(field).map(value => ({ label: value, value })),
    ]));
  }

  get selectedFilter(): BoardFilterSet | null {
    return this.filterSets.find((filter) => filter.id === this.selectedFilterId) ?? null;
  }

  fieldValueOptions(field: FilterFieldOption | null): { label: string; value: string }[] {
    return field ? this.valueOptionsByKey.get(this.fieldKey(field)) ?? [] : [];
  }

  get hasConditions(): boolean {
    return this.conditions.some((condition) => this.serializeCondition(condition));
  }

  get hasUnsavedChanges(): boolean {
    return !!this.selectedFilterId && this.getDraftSnapshot() !== this.savedDraftSnapshot;
  }

  get canSave(): boolean {
    return this.initialized && !this.loading && (this.entityMode !== 'card' || !!this.funnelId) && !!this.draftName.trim() && this.hasConditions && !this.saving
      && (!this.selectedFilterId || this.hasUnsavedChanges);
  }

  private get filterContext(): { type: string; api: FilterApi } {
    switch (this.entityMode) {
      case 'contact':
        return { type: 'CONTACT_V2', api: this.contactService };
      case 'company':
        return { type: 'COMPANY_V2', api: this.companyService };
      case 'card':
        return {
          type: this.funnelType,
          api: {
            getFilters: () => this.boardService.getFilters(this.funnelId!),
            createFilter: payload => this.boardService.createFilter({ ...payload, funnel_id: this.funnelId! }),
            updateFilter: (id, payload) => this.boardService.updateFilter(id, { ...payload, funnel_id: this.funnelId! }),
            deleteFilter: id => this.boardService.deleteFilter(id),
          },
        };
    }
  }

  loadFilterSets(): void {
    this.initialized = false;
    if (this.entityMode === 'card' && !this.funnelId) {
      this.loading = false;
      return;
    }
    const funnelId = this.funnelId;
    this.loading = true;
    this.filterContext.api.getFilters()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => { if (this.funnelId === funnelId) this.loading = false; }),
      )
      .subscribe({
        next: (filters) => {
          if (this.funnelId !== funnelId) return;
          this.filterSets = filters ?? [];
          const activeFilter = this.filterSets.find(filter => filter.id === this.activeFilterId);
          this.selectFilter(activeFilter?.id ?? null);
          this.resetDraftFromData(this.activeData);
          this.initialized = true;
        },
        error: () => {
          this.notificationService.showError('', String(this.translateService.instant('board_v3_filter.load_failed')));
        },
      });
  }

  selectFilter(filterId: string | null): void {
    this.selectedFilterId = filterId || null;
    const filter = this.selectedFilter;
    if (!filter) {
      this.draftName = '';
      this.draftShared = false;
      this.resetDraftFromData(this.activeData);
      return;
    }

    this.draftName = filter.name;
    this.draftShared = !!filter.is_shared;
    this.resetDraftFromData(normalizeBoardFilterData(filter.x_data));
    this.savedDraftSnapshot = this.getDraftSnapshot();
  }

  addCondition(): void {
    const field = this.allFields[0];
    if (!field) return;
    const operator = operatorsForField(field)[0]?.value || 'eq';
    this.conditions = [
      ...this.conditions,
      {
        localId: this.createLocalId(),
        key: this.fieldKey(field),
        operator,
        value: null,
      },
    ];
  }

  removeCondition(condition: DraftCondition): void {
    this.conditions = this.conditions.filter((item) => item.localId !== condition.localId);
  }

  onFieldChange(condition: DraftCondition): void {
    const field = this.findField(condition.key);
    condition.operator = field ? operatorsForField(field)[0]?.value || 'eq' : 'eq';
    condition.value = null;
    condition.valueTo = null;
  }

  onOperatorChange(condition: DraftCondition): void {
    const operator = this.operatorForCondition(condition);
    condition.value = operator?.isMulti ? [] : null;
    condition.valueTo = null;
  }

  operatorsForCondition(condition: DraftCondition): FilterOperator[] {
    const field = this.findField(condition.key);
    return field ? operatorsForField(field) : this.baseOperators['text'];
  }

  operatorForCondition(condition: DraftCondition): FilterOperator | undefined {
    return this.operatorsForCondition(condition).find((operator) => operator.value === condition.operator);
  }

  fieldForCondition(condition: DraftCondition): FilterFieldOption | null {
    return this.findField(condition.key);
  }

  readonly fieldKey = filterFieldKey;

  fieldLabel(field: FilterFieldOption): string {
    return field.is_system ? String(this.translateService.instant(field.name)) : field.name;
  }

  fieldOptions(field: FilterFieldOption | null): string[] {
    return Array.isArray(field?.options) ? field.options.map((option) => String(option)) : [];
  }

  applyDraft(): void {
    if (!this.initialized || this.loading) return;
    this.apply.emit({ data: this.getDraftData(), name: this.getDraftDisplayName(), filterId: this.selectedFilterId });
  }

  resetDraft(): void {
    this.selectedFilterId = null;
    this.draftName = '';
    this.draftShared = false;
    this.resetDraftFromData(normalizeBoardFilterData());
    this.apply.emit({ data: this.getDraftData(), name: '', filterId: null });
  }

  saveDraft(): void {
    if (!this.canSave) return;

    const { api, type } = this.filterContext;
    const payload: SaveBoardFilterPayload & { type: string } = {
      name: this.draftName.trim(),
      type,
      is_shared: this.draftShared,
      x_data: this.getDraftData(),
    };

    const savedSnapshot = this.getDraftSnapshot();
    this.saving = true;
    const request = this.selectedFilterId
      ? api.updateFilter(this.selectedFilterId, payload)
      : api.createFilter(payload);

    request
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (filter) => {
          this.selectedFilterId = filter.id;
          this.draftName = filter.name;
          this.draftShared = !!filter.is_shared;
          this.upsertFilter(filter);
          this.savedDraftSnapshot = savedSnapshot;
          this.notificationService.showSuccess(
            '',
            String(this.translateService.instant('notifications_texts.success.successfully_saved')),
          );
        },
        error: () => {
          this.notificationService.showError(
            '',
            String(this.translateService.instant('notifications_texts.error.error_saving')),
          );
        },
      });
  }

  deleteSelected(): void {
    if (!this.selectedFilterId || this.deleting) return;
    const deletedId = this.selectedFilterId;
    this.deleting = true;
    this.filterContext.api
      .deleteFilter(deletedId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.deleting = false)),
      )
      .subscribe({
        next: () => {
          this.filterSets = this.filterSets.filter((filter) => filter.id !== deletedId);
          this.selectFilter(null);
          this.notificationService.showSuccess(
            '',
            String(this.translateService.instant('notifications_texts.success.successfully_deleted')),
          );
        },
        error: () => {
          this.notificationService.showError(
            '',
            String(this.translateService.instant('notifications_texts.error.error_while_delete_1')),
          );
        },
      });
  }

  private findField(key: string): FilterFieldOption | null {
    return this.fieldsByKey.get(key) ?? null;
  }

  private resetDraftFromData(data: BoardFilterSetData | null | undefined): void {
    const normalized = normalizeBoardFilterData(data);
    this.mode = normalized.mode;
    this.view = { ...normalized.view };
    this.conditions = normalized.conditions.map(condition => deserializeFilterCondition(condition, this.createLocalId()));
    if (!this.conditions.length) this.addCondition();
  }

  private getDraftSnapshot(): string {
    return JSON.stringify({ name: this.draftName.trim(), is_shared: this.draftShared, data: this.getDraftData() });
  }

  private getDraftData(): BoardFilterSetData {
    return serializeFilterDraft(this.mode, this.view, this.conditions, this.fieldsByKey);
  }

  private serializeCondition(condition: DraftCondition): BoardFilterCondition | null {
    return serializeFilterCondition(condition, this.fieldsByKey);
  }

  private getDraftDisplayName(): string {
    return this.draftName.trim() || '';
  }

  private createLocalId(): string {
    return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }

  private upsertFilter(filter: BoardFilterSet): void {
    const index = this.filterSets.findIndex((item) => item.id === filter.id);
    if (index === -1) {
      this.filterSets = [...this.filterSets, filter];
      return;
    }

    const next = [...this.filterSets];
    next[index] = filter;
    this.filterSets = next;
  }
}
